"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findRetrieves = void 0;
const ELMDependencyHelpers_1 = require("./ELMDependencyHelpers");
const ELMHelpers_1 = require("./ELMHelpers");
const CustomErrors_1 = require("../../types/errors/CustomErrors");
/**
 * List of possible expressions that could be doing extra filtering on the result of a query
 */
const VALUE_COMPARISON_TYPES = ['Greater', 'IsNull'];
/*
 * Helper function for recursing on the findRetrieves function
 * Allows for explicit specification of which arguments change on each recursive call
 * NOTE: This function has a side effect of updating the results array that gets defined in findRetrieves
 */
function recurse(results, recursedExpression, opts) {
    const retrieves = findRetrieves(opts.elm, opts.allELM, recursedExpression, opts.newQueryLocalId, opts.newValueComparisonLocalId, [...opts.expressionStack], opts.withErrors);
    results.push(...retrieves.results);
}
/**
 * Get all data types, and codes/valuesets used in Query ELM expressions
 *
 * @param elm main ELM library with expressions to traverse
 * @param allELM list of any dependent ELM libraries included in the main ELM
 * @param expr expression to find queries under (usually numerator for gaps in care)
 * @param queryLocalId keeps track of latest id of query statements for lookup later
 * @returns query info for each ELM retrieve
 */
function findRetrieves(elm, allELM, expr, queryLocalId, valueComparisonLocalId, expressionStack = [], withErrors = []) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    // Smart defaults for recursive call to avoid passing in a bunch of values that don't usually change
    const defaultRecursiveOpts = {
        elm,
        allELM,
        newQueryLocalId: queryLocalId,
        newValueComparisonLocalId: valueComparisonLocalId,
        expressionStack,
        withErrors
    };
    const results = [];
    // Push this expression onto the stack of processed expressions
    if (expr.localId && expr.type) {
        expressionStack.push({
            libraryName: elm.library.identifier.id,
            localId: expr.localId,
            type: expr.type
        });
    }
    // Base case, get data type and code/valueset off the expression
    if (expr.type === 'Retrieve' && expr.dataType) {
        const exprRet = expr;
        // If present, strip off HL7 prefix to data type
        const dataType = exprRet.dataType.replace(/^(\{http:\/\/hl7.org\/fhir\})?/, '');
        const templateId = exprRet.templateId;
        let queryLibraryName = elm.library.identifier.id;
        // We need to detect if this query/retrieve is used as the source of another query directly. This is an indication
        // that the retrieve is filtered by two separate queries and we need to actually look at the 'outermost' query closest
        // to the numerator.
        // This looks like the expressionStack ends with the following types ['Query', 'ExpressionRef', 'Query', 'Retrieve']
        if (expressionStack.length >= 4) {
            // Grab the last 4 in the stack and see if they match the case we are looking for
            const bottomExprs = expressionStack.slice(-4);
            if (bottomExprs[0].type === 'Query' &&
                bottomExprs[1].type === 'ExpressionRef' &&
                bottomExprs[2].type === 'Query') {
                // check if the outer query is indeed referencing the inner one in the source.
                const queryLib = allELM.find(lib => lib.library.identifier.id === bottomExprs[0].libraryName);
                if (!queryLib) {
                    throw new CustomErrors_1.UnexpectedResource('Referenced query library cannot be found.');
                }
                const outerQuery = (0, ELMHelpers_1.findClauseInLibrary)(queryLib, bottomExprs[0].localId);
                if (outerQuery.source[0].expression.localId === bottomExprs[1].localId &&
                    outerQuery.source[0].expression.type === 'ExpressionRef') {
                    // Change the queryLocalId to the outer query.
                    queryLocalId = bottomExprs[0].localId;
                    queryLibraryName = bottomExprs[0].libraryName;
                }
                else {
                    withErrors.push({
                        message: 'Query is referenced in another query but not as a single source. Gaps output may be incomplete.',
                        localId: queryLocalId
                    });
                }
            }
        }
        if (((_a = exprRet.codes) === null || _a === void 0 ? void 0 : _a.type) === 'ValueSetRef') {
            const codes = exprRet.codes;
            const valueSet = (0, ELMDependencyHelpers_1.findValueSetReference)(elm, allELM, codes);
            if (valueSet) {
                results.push({
                    dataType,
                    valueSet: valueSet.id,
                    queryLocalId,
                    valueComparisonLocalId,
                    retrieveLocalId: exprRet.localId,
                    retrieveLibraryName: elm.library.identifier.id,
                    queryLibraryName,
                    expressionStack: [...expressionStack],
                    path: exprRet.codeProperty,
                    templateId
                });
            }
        }
        else if (((_b = exprRet.codes) === null || _b === void 0 ? void 0 : _b.type) === 'CodeRef' ||
            (((_c = exprRet.codes) === null || _c === void 0 ? void 0 : _c.type) === 'ToList' && ((_d = exprRet.codes.operand) === null || _d === void 0 ? void 0 : _d.type) === 'CodeRef')) {
            // ToList promotions have the CodeRef on the operand
            const codeName = exprRet.codes.type === 'CodeRef'
                ? exprRet.codes.name
                : exprRet.codes.operand.name;
            const code = (_e = elm.library.codes) === null || _e === void 0 ? void 0 : _e.def.find(c => c.name === codeName);
            if (code) {
                const cs = (_f = elm.library.codeSystems) === null || _f === void 0 ? void 0 : _f.def.find(cs => cs.name == code.codeSystem.name);
                results.push({
                    dataType,
                    code: {
                        system: (cs === null || cs === void 0 ? void 0 : cs.id) || code.codeSystem.name,
                        version: cs === null || cs === void 0 ? void 0 : cs.version,
                        display: code.display,
                        code: code.id
                    },
                    queryLocalId,
                    valueComparisonLocalId,
                    retrieveLocalId: exprRet.localId,
                    retrieveLibraryName: elm.library.identifier.id,
                    queryLibraryName,
                    expressionStack: [...expressionStack],
                    path: exprRet.codeProperty,
                    templateId
                });
            }
        }
    }
    else if (expr.type === 'Query') {
        // For queries, recurse on all cases that may contain any ELM expression
        // NOTE: ignoring "aggregate" for now, as we have no precedent for its use within queries for eCQMs
        const query = expr;
        // Overwrite queryLocalId with new ID of the current query expression
        const recursiveOpts = Object.assign(Object.assign({}, defaultRecursiveOpts), { newQueryLocalId: query.localId });
        query.source.forEach(s => {
            recurse(results, s.expression, recursiveOpts);
        });
        (_g = query.let) === null || _g === void 0 ? void 0 : _g.forEach(letClause => {
            recurse(results, letClause.expression, recursiveOpts);
        });
        if (query.where) {
            recurse(results, query.where, recursiveOpts);
        }
        if (query.return) {
            recurse(results, query.return.expression, recursiveOpts);
        }
        (_h = query.relationship) === null || _h === void 0 ? void 0 : _h.forEach(relationshipClause => {
            recurse(results, relationshipClause.expression, recursiveOpts);
            recurse(results, relationshipClause.suchThat, recursiveOpts);
        });
    }
    else if (expr.type === 'ExpressionRef') {
        // Find expression in dependent library
        if (expr.libraryName) {
            const matchingLib = (0, ELMDependencyHelpers_1.findLibraryReference)(elm, allELM, expr.libraryName || '');
            const exprRef = matchingLib === null || matchingLib === void 0 ? void 0 : matchingLib.library.statements.def.find(e => e.name === expr.name);
            if (matchingLib && exprRef) {
                // Overwrite default ELM with new ELM containing the referenced expression
                recurse(results, exprRef.expression, Object.assign(Object.assign({}, defaultRecursiveOpts), { elm: matchingLib }));
            }
        }
        else {
            // Find expression in current library
            const exprRef = elm.library.statements.def.find(d => d.name === expr.name);
            if (exprRef) {
                recurse(results, exprRef.expression, defaultRecursiveOpts);
            }
        }
    }
    else if (expr.operand) {
        // Operand can be array or object. Recurse on either
        const anyExpr = expr;
        const newValueComparisonLocalId = VALUE_COMPARISON_TYPES.includes(expr.type)
            ? expr.localId
            : valueComparisonLocalId;
        // Overwrite the valueComparisonLocalId with the newly found value expression
        const recursiveOpts = Object.assign(Object.assign({}, defaultRecursiveOpts), { newValueComparisonLocalId });
        if (Array.isArray(anyExpr.operand)) {
            // Should expand to types beyond greater
            anyExpr.operand.forEach((e) => {
                recurse(results, e, recursiveOpts);
            });
        }
        else {
            recurse(results, anyExpr.operand, recursiveOpts);
        }
    }
    else if (expr.type === 'Tuple') {
        const tuple = expr;
        tuple.element.forEach(te => {
            recurse(results, te.value, defaultRecursiveOpts);
        });
    }
    else if (expr.source) {
        // Pass through the source expression if no other cases have been satisfied
        recurse(results, expr.source, defaultRecursiveOpts);
    }
    return { results, withErrors };
}
exports.findRetrieves = findRetrieves;
//# sourceMappingURL=RetrievesHelper.js.map